import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";

interface UseParkedImportSocketParams {
  /**
   * Called when a parked import batch is created, approved or discarded anywhere. The
   * payload is only a refresh signal — re-read the list over REST rather than trusting it.
   */
  onChange: (payload: { chapter_id: number | null }) => void;
  enabled?: boolean;
}

/**
 * Keeps the Project Fairs parked-import queue live.
 *
 * The queue used to be fetched only on mount, so a batch submitted by another officer
 * never appeared for a reviewer already sitting on the page — they had to reload the
 * browser. The server emits to `role:admin` and to the batch's `chapter:<id>` room, which
 * matches listParkedImports' own authorization, so simply reacting to anything that
 * arrives here is safe; the follow-up REST call is still the authority on what this user
 * may see.
 *
 * `onChange` is kept in a ref so a caller passing an inline arrow function doesn't tear
 * down and re-establish the socket on every render.
 */
export function useParkedImportSocket({ onChange, enabled = true }: UseParkedImportSocketParams) {
  const socketRef = useRef<Socket | null>(null);
  const handlerRef = useRef(onChange);
  handlerRef.current = onChange;

  useEffect(() => {
    if (!enabled) return;

    const socket = io({
      path: "/socket.io/",
      transports: ["websocket", "polling"],
      withCredentials: true,
    });
    socketRef.current = socket;

    const handle = (payload: { chapter_id: number | null }) => handlerRef.current(payload);
    socket.on("project-fair-parked-import:created", handle);
    socket.on("project-fair-parked-import:resolved", handle);

    return () => {
      socket.off("project-fair-parked-import:created", handle);
      socket.off("project-fair-parked-import:resolved", handle);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled]);

  return socketRef.current;
}
